/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, DOCUMENT, Component, ElementRef, Inject, Input, OnInit, signal} from '@angular/core';

import template from './creative-preview.component.html';
import {CreativeViewer} from '../../../components/util/creative-viewer';
import {LocalCreativePreview} from '../../../components/session/local-creative-preview-data';
import {Uuid} from '../../../components/util/uuid';
import {
  hasSpecificPreviewLanguageSelection,
  normalizePreviewLanguageSelection,
  PREVIEW_LANGUAGE_OPTIONS,
  resolvePreviewLanguageCode,
  shouldShowPreviewLanguageSelector
} from './preview-language';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {WINDOW} from '../../angular-platform-providers';
import {StateService} from '@uirouter/core';
import {notifySignalView} from '../../common/util/template-state';

@Component({
  standalone: false,
  selector: 'adn-creative-preview',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreativePreviewComponent implements OnInit {
  @Input() previewTrigger: any;
  @Input() layoutType: string;
  @Input() creativeId: string;
  @Input() lineItemId: string;
  @Input() lineItemCode: string;
  @Input() creative: any;

  showBorder = true;
  blankPageOnly = false;
  blankPage = false;
  simpleOnly = false;
  resolved = true;
  dimensionMessage = '';
  flexiblePreviewDimensions = false;
  showFlexiblePreviewDimensions = false;
  previewDimensionWidth: number;
  previewDimensionHeight: number;
  currentPreviewWidth: number;
  currentPreviewHeight: number;
  previewDimensionError = '';
  showLanguageSelector = false;
  previewLanguage = 'browser';
  hasUserTargeting = false;
  networkId: string;
  uuid: string;
  cookiePreviewData: any = {};
  readonly previewLanguageOptions = PREVIEW_LANGUAGE_OPTIONS;
  readonly js = {
    showJsMessage: true,
    enabled: false
  };
  includeLinksInJsLabel = false;
  readonly viewRevision = signal(0);

  private thisPreviewData: any;

  constructor(
    private readonly elementRef: ElementRef,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(WINDOW) private readonly window: any,
    private readonly $state: StateService
  ) {
  }

  ngOnInit(): void {
    this.previewLanguage = normalizePreviewLanguageSelection(this.previewLanguage);
    this.showLanguageSelector = shouldShowPreviewLanguageSelector(this.creative);
    this.includeLinksInJsLabel = !!this.LocalNetworkProfile.isPoliticalAdsPlatform();
    this.networkId = this.LocalNetworkProfile.getNetworkTag();
    this.uuid = Uuid.generate(this.creativeId);

    if (!this.previewTrigger) {
      this.previewTrigger = {};
    }

    this.previewTrigger.previewInline = (previewData: any, apiPreviewData: any, blankPageOnly?: boolean) => {
      this.blankPageOnly = !!blankPageOnly;
      if (!previewData || !previewData.creativeContent || !previewData.creativeWidth || !previewData.creativeHeight) {
        return;
      }
      this.showLanguageSelector = shouldShowPreviewLanguageSelector(this.creative, previewData.creativeContent);
      this.thisPreviewData = previewData;
      this.thisPreviewData.showBorder = this.showBorder;
      this.currentPreviewWidth = Number(previewData.creativeWidth);
      this.currentPreviewHeight = Number(previewData.creativeHeight);

      if (!previewData.maxWidth) {
        this.blankPage = true;
      }

      // Full-size previews (modals, editors) always offer Enable JavaScript.
      // Thumbnails pass maxWidth and keep the control hidden.
      this.js.showJsMessage = !previewData.maxWidth;

      this.flexiblePreviewDimensions = previewData.dimensionType === 'FLEXIBLE';
      if (previewData.dimensionType === 'FLEXIBLE') {
        this.dimensionMessage = '';
        if (!this.showFlexiblePreviewDimensions) {
          this.previewDimensionWidth = this.currentPreviewWidth;
          this.previewDimensionHeight = this.currentPreviewHeight;
        }
      } else if (previewData.dimensionType === 'FIXED_WIDTH') {
        this.dimensionMessage = 'Using height of ' + previewData.creativeHeight + ' pixels for this preview';
        this.showFlexiblePreviewDimensions = false;
      } else if (previewData.dimensionType === 'FIXED_HEIGHT') {
        this.dimensionMessage = 'Using width of ' + previewData.creativeWidth + ' pixels for this preview';
        this.showFlexiblePreviewDimensions = false;
      } else {
        this.dimensionMessage = '';
        this.showFlexiblePreviewDimensions = false;
      }
      this.previewDimensionError = '';

      previewData.allowScript = this.js.enabled;
      previewData.dataLang = this.getSelectedLanguageCode();
      previewData.dataLangAttribute = hasSpecificPreviewLanguageSelection(this.previewLanguage)
        ? this.getSelectedLanguageCode()
        : null;
      this.hasUserTargeting = !!apiPreviewData?.hasUserTargeting;
      this.cookiePreviewData = Object.assign({}, apiPreviewData, {
        creativeContent: blankPageOnly ? previewData.creativeContent : undefined,
        // Blank-page preview always allows scripts. The inline checkbox only
        // applies to CreativeViewer, not /preview-creative.
        allowScript: true,
        dataLang: this.getSelectedLanguageCode(),
        previewLanguage: normalizePreviewLanguageSelection(this.previewLanguage),
        showLanguageSelector: this.showLanguageSelector,
        hasUserTargeting: this.hasUserTargeting
      });
      if (this.blankPageOnly) {
        this.blankPage = true;
        notifySignalView(this.viewRevision);
        return;
      }
      const divContainer = this.elementRef.nativeElement.querySelector('.previewContainer');
      new CreativeViewer(this.document).viewCreative(divContainer, previewData);
      notifySignalView(this.viewRevision);
    };
  }

  get blankPageParams(): any {
    return {
      uuid: this.uuid,
      creativeId: this.creativeId,
      creativeWidth: this.cookiePreviewData.creativeWidth || '',
      creativeHeight: this.cookiePreviewData.creativeHeight || '',
      networkId: this.networkId,
      layoutType: this.layoutType,
      lineItemId: this.cookiePreviewData.lineItemId || this.lineItemId,
      lineItemCode: this.cookiePreviewData.lineItemCode || this.lineItemCode,
      dataLang: this.cookiePreviewData.dataLang || '',
      previewLanguage: this.cookiePreviewData.previewLanguage || 'browser'
    };
  }

  get blankPageHref(): string {
    return this.$state.href('nude.preview', this.blankPageParams, {absolute: true}) || '';
  }

  getSelectedLanguageCode(): string | null {
    return this.showLanguageSelector
      ? resolvePreviewLanguageCode(this.previewLanguage, this.window.navigator)
      : null;
  }

  onPreviewLanguageChange(): void {
    this.previewLanguage = normalizePreviewLanguageSelection(this.previewLanguage);
    this.preview();
  }

  onHasUserTargetingChange(): void {
    if (this.cookiePreviewData) {
      this.cookiePreviewData.hasUserTargeting = !!this.hasUserTargeting;
    }
    if (this.previewTrigger?.refreshPreview) {
      this.previewTrigger.refreshPreview({
        hasUserTargeting: this.hasUserTargeting,
        creativeWidth: this.currentPreviewWidth,
        creativeHeight: this.currentPreviewHeight
      });
    }
  }

  openFlexiblePreviewDimensions(): void {
    this.previewDimensionWidth = this.currentPreviewWidth;
    this.previewDimensionHeight = this.currentPreviewHeight;
    this.previewDimensionError = '';
    this.showFlexiblePreviewDimensions = true;
  }

  submitFlexiblePreviewDimensions(): void {
    const creativeWidth = Number(this.previewDimensionWidth);
    const creativeHeight = Number(this.previewDimensionHeight);

    if (!Number.isFinite(creativeWidth) || creativeWidth < 1 || !Number.isFinite(creativeHeight) || creativeHeight < 1) {
      this.previewDimensionError = 'Enter a width and height greater than 0.';
      return;
    }

    this.currentPreviewWidth = creativeWidth;
    this.currentPreviewHeight = creativeHeight;
    if (this.thisPreviewData) {
      this.thisPreviewData.creativeWidth = creativeWidth;
      this.thisPreviewData.creativeHeight = creativeHeight;
    }
    if (this.cookiePreviewData) {
      this.cookiePreviewData.creativeWidth = creativeWidth;
      this.cookiePreviewData.creativeHeight = creativeHeight;
    }

    this.previewDimensionError = '';
    this.showFlexiblePreviewDimensions = false;
    if (this.previewTrigger?.refreshPreview) {
      this.previewTrigger.refreshPreview({
        hasUserTargeting: this.hasUserTargeting,
        creativeWidth,
        creativeHeight
      });
      return;
    }
    this.preview();
  }

  cancelFlexiblePreviewDimensions(): void {
    this.previewDimensionWidth = this.currentPreviewWidth;
    this.previewDimensionHeight = this.currentPreviewHeight;
    this.previewDimensionError = '';
    this.showFlexiblePreviewDimensions = false;
  }

  preview(): void {
    this.resolved = false;
    if (this.thisPreviewData) {
      this.thisPreviewData.dataLang = this.getSelectedLanguageCode();
      this.thisPreviewData.dataLangAttribute = hasSpecificPreviewLanguageSelection(this.previewLanguage)
        ? this.getSelectedLanguageCode()
        : null;
    }
    if (this.cookiePreviewData) {
      this.cookiePreviewData.dataLang = this.getSelectedLanguageCode();
      this.cookiePreviewData.previewLanguage = normalizePreviewLanguageSelection(this.previewLanguage);
      this.cookiePreviewData.showLanguageSelector = this.showLanguageSelector;
    }
    if (!this.blankPageOnly && this.previewTrigger?.previewInline && this.thisPreviewData) {
      this.previewTrigger.previewInline(this.thisPreviewData, this.cookiePreviewData);
    }
    this.resolved = true;
    notifySignalView(this.viewRevision);
  }

  setCookieInfo(): void {
    new LocalCreativePreview().add(this.uuid, this.LocalNetworkProfile.getNetworkTag(), this.cookiePreviewData);
  }
}
